import { hashString } from "@/api/mock-data/agent-estate/generate";
import type {
  AuthorizationProvider,
  CredentialAvailability,
  ManagedCredentialOption,
  ProfileCheck,
  ProfileCheckState,
  ProfileInput,
  ProfileMetadataPreview,
  ProfileReasonCode,
} from "@/api/temp/OAuthClientProfileService";
import { isConcreteIssuer } from "@/pages/Administration/OAuthClientProfiles/profileModel";

/**
 * The mock backend validator. A real one fetches the document through the
 * protected outbound fetcher (URL policy, no redirects, size/time limits, no
 * forwarded cookies or tokens) and validates it against the pinned CIMD draft;
 * this one derives a deterministic result from the URL so every scenario is
 * reproducible:
 *
 *   `blocked`       → the destination is not permitted by tenant policy
 *   `redirect`      → the URL redirects (rejected, never followed)
 *   `timeout`       → the document could not be retrieved
 *   `not-metadata`  → the response is not a client metadata document
 *   `secret`        → the document carries credential material (not echoed)
 *   `mismatch`      → the document's client_id differs from the URL
 *   `public-client` → a public client: no client authentication applies
 *
 * Nothing is fetched and no remote body is ever returned: the preview is an
 * allowlist of public fields, and a secret-bearing document yields no preview.
 */

export const documentFingerprintOf = (url: string, version: number) =>
  `doc-${hashString(url).toString(16)}-v${version}`;

const isHttpsUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.hash && url.pathname.length > 1;
  } catch {
    return false;
  }
};

export interface CheckOutcome {
  state: ProfileCheckState;
  checks: ProfileCheck[];
  preview?: ProfileMetadataPreview;
}

const overallState = (checks: ProfileCheck[]): ProfileCheckState => {
  if (checks.some(check => check.state === "FAILED")) return "FAILED";
  if (checks.some(check => check.state === "UNAVAILABLE")) return "UNAVAILABLE";
  if (checks.some(check => check.state === "NOT_CHECKED")) return "NOT_CHECKED";
  return "PASSED";
};

const credentialCheck = (
  credential: ManagedCredentialOption | undefined,
  ref: string | undefined,
  required: boolean,
): ProfileCheck => {
  const code = "MANAGED_AUTHENTICATION" as const;
  // The profile form does not collect a managed credential: with no
  // reference there is nothing to check, which is not a failure.
  if (!required || !ref) return { code, state: "NOT_APPLICABLE" };
  if (!credential) {
    return { code, state: "FAILED", reasonCode: "AUTH_REFERENCE_MISSING" };
  }
  if (credential.availability === "RESTRICTED") {
    return { code, state: "FAILED", reasonCode: "AUTH_REFERENCE_RESTRICTED" };
  }
  return { code, state: "PASSED" };
};

function checkCimd(
  input: ProfileInput,
  credential: ManagedCredentialOption | undefined,
  documentVersion: number,
  now: number,
): CheckOutcome {
  const url =
    input.registration.kind === "CIMD" ? (input.registration.clientIdUrl ?? "") : "";
  const retrieval = (
    state: ProfileCheckState,
    reasonCode?: ProfileReasonCode,
  ): ProfileCheck => ({ code: "METADATA_RETRIEVAL", state, reasonCode });
  const notChecked = (code: ProfileCheck["code"]): ProfileCheck => ({
    code,
    state: "NOT_CHECKED",
  });
  const unreached = [
    notChecked("DOCUMENT_VALIDATION"),
    notChecked("CLIENT_ID_MATCH"),
    notChecked("MANAGED_AUTHENTICATION"),
  ];

  if (!isHttpsUrl(url)) {
    const checks = [retrieval("FAILED", "INVALID_URL"), ...unreached];
    return { state: "FAILED", checks };
  }
  if (url.includes("blocked")) {
    const checks = [retrieval("FAILED", "UNSAFE_DESTINATION"), ...unreached];
    return { state: "FAILED", checks };
  }
  if (url.includes("redirect")) {
    const checks = [retrieval("FAILED", "REDIRECT_REJECTED"), ...unreached];
    return { state: "FAILED", checks };
  }
  if (url.includes("timeout")) {
    const checks = [retrieval("UNAVAILABLE", "UNREACHABLE"), ...unreached];
    return { state: "UNAVAILABLE", checks };
  }
  if (url.includes("not-metadata") || url.includes("secret")) {
    const checks: ProfileCheck[] = [
      retrieval("PASSED"),
      {
        code: "DOCUMENT_VALIDATION",
        state: "FAILED",
        reasonCode: url.includes("secret")
          ? "FORBIDDEN_CREDENTIAL_DATA"
          : "NOT_METADATA_DOCUMENT",
      },
      notChecked("CLIENT_ID_MATCH"),
      notChecked("MANAGED_AUTHENTICATION"),
    ];
    // The offending document is never returned, not even as an error preview.
    return { state: "FAILED", checks };
  }

  const publicClient = url.includes("public-client");
  const clientId = url.includes("mismatch")
    ? url.replace("mismatch", "other-client")
    : url;
  const authMethod = publicClient ? "none" : "private_key_jwt";
  const grantTypes = ["client_credentials"];
  const origin = new URL(url).origin;
  const document: Record<string, unknown> = {
    client_id: clientId,
    client_name: `${new URL(url).hostname} client`,
    grant_types: grantTypes,
    token_endpoint_auth_method: authMethod,
    ...(publicClient ? {} : { jwks_uri: `${origin}/oauth/jwks.json` }),
  };
  const documentFingerprint = documentFingerprintOf(url, documentVersion);
  const checks: ProfileCheck[] = [
    retrieval("PASSED"),
    { code: "DOCUMENT_VALIDATION", state: "PASSED" },
    clientId === url
      ? { code: "CLIENT_ID_MATCH", state: "PASSED" }
      : { code: "CLIENT_ID_MATCH", state: "FAILED", reasonCode: "CLIENT_ID_MISMATCH" },
    credentialCheck(credential, input.managedCredentialRef, !publicClient),
  ];
  // A key-backed document needs a signing key, not a shared secret.
  if (
    !publicClient &&
    credential &&
    credential.availability === "USABLE" &&
    credential.method !== "private_key_jwt"
  ) {
    checks[3] = {
      code: "MANAGED_AUTHENTICATION",
      state: "FAILED",
      reasonCode: "KEY_BINDING_NOT_ESTABLISHED",
    };
  }
  return {
    state: overallState(checks),
    checks,
    preview: {
      clientName: String(document.client_name),
      clientId,
      tokenEndpointAuthMethod: authMethod,
      grantTypes,
      redirectUris: [],
      jwksUri: document.jwks_uri as string | undefined,
      document,
      fetchedAt: new Date(now).toISOString(),
      documentFingerprint,
    },
  };
}

/**
 * Issuer binding for a registered application: the selected provider
 * integration must have a concrete configured issuer. A documented format, a
 * missing issuer or a reported mismatch never counts - and nothing is
 * rewritten to make it pass.
 */
function issuerBinding(
  registration: Extract<ProfileInput["registration"], { kind: "EXISTING_OAUTH_CLIENT" }>,
  provider: AuthorizationProvider | undefined,
  knownIssuers: string[],
): ProfileCheck {
  const code = "ISSUER_BINDING" as const;
  if (registration.providerId) {
    if (!provider) return { code, state: "FAILED", reasonCode: "ISSUER_UNKNOWN" };
    if (!isConcreteIssuer(provider.issuer)) {
      return { code, state: "FAILED", reasonCode: "ISSUER_NOT_CONFIGURED" };
    }
    if (provider.verification === "MISMATCH") {
      return { code, state: "FAILED", reasonCode: "ISSUER_MISMATCH" };
    }
    if (provider.verification === "UNAVAILABLE") {
      return { code, state: "UNAVAILABLE", reasonCode: "VALIDATION_UNAVAILABLE" };
    }
    return { code, state: "PASSED" };
  }
  // A record saved before provider references: its issuer, as saved.
  return registration.issuer && knownIssuers.includes(registration.issuer)
    ? { code, state: "PASSED" }
    : { code, state: "FAILED", reasonCode: "ISSUER_UNKNOWN" };
}

function checkExistingClient(
  input: ProfileInput,
  credential: ManagedCredentialOption | undefined,
  provider: AuthorizationProvider | undefined,
  knownIssuers: string[],
): CheckOutcome {
  if (input.registration.kind !== "EXISTING_OAUTH_CLIENT") {
    return { state: "FAILED", checks: [] };
  }
  const { registration } = input;
  const bound = registration.providerId ?? registration.issuer;
  // A local completeness check - "Details complete", never "Registered".
  const checks: ProfileCheck[] = [
    registration.clientId && bound
      ? { code: "DETAILS_COMPLETE", state: "PASSED" }
      : { code: "DETAILS_COMPLETE", state: "FAILED", reasonCode: "DETAILS_INCOMPLETE" },
    issuerBinding(registration, provider, knownIssuers),
    credentialCheck(credential, input.managedCredentialRef, true),
  ];
  return { state: overallState(checks), checks };
}

export function runProfileChecks(
  input: ProfileInput,
  options: {
    credentials: ManagedCredentialOption[];
    providers: AuthorizationProvider[];
    knownIssuers: string[];
    documentVersion: number;
    now: number;
  },
): CheckOutcome {
  const credential = options.credentials.find(
    entry => entry.id === input.managedCredentialRef,
  );
  if (input.registration.kind === "CIMD") {
    return checkCimd(input, credential, options.documentVersion, options.now);
  }
  const { providerId } = input.registration;
  const provider = providerId
    ? options.providers.find(entry => entry.id === providerId)
    : undefined;
  return checkExistingClient(input, credential, provider, options.knownIssuers);
}

export const credentialAvailabilityOf = (
  credentials: ManagedCredentialOption[],
  ref: string | undefined,
  fallback: CredentialAvailability = "NOT_CHECKED",
): CredentialAvailability => {
  if (!ref) return fallback;
  return credentials.find(entry => entry.id === ref)?.availability ?? "MISSING";
};
